import type { SupportedLocale } from '../types'

const COOKIE_CANDIDATES = [
  'wp-wpml_current_language', // WPML modern
  'wpml_current_language',
  '_icl_current_language', // WPML legacy
  'icl_current_language',
]

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const cookies = document.cookie ? document.cookie.split('; ') : []
  for (const c of cookies) {
    if (!c.startsWith(name + '=')) continue
    const raw = c.slice(name.length + 1)
    try {
      return decodeURIComponent(raw)
    } catch {
      return raw
    }
  }
  return undefined
}

function detectBrowserLocale(): SupportedLocale {
  if (typeof navigator === 'undefined') return 'en'

  // Try navigator.languages first (most preferred), then navigator.language
  const browserLangs = navigator.languages || [navigator.language || 'en']

  const supported: SupportedLocale[] = ['en', 'sv', 'es', 'de', 'fr', 'nl', 'fi', 'no', 'da', 'pl']

  for (const lang of browserLangs) {
    const base = lang.trim().toLowerCase().split(/[-_]/)[0] ?? ''

    // Normalize browser language codes
    const normalized =
      base === 'se' ? 'sv' : base === 'nb' || base === 'nn' ? 'no' : base === 'dk' ? 'da' : base

    if (supported.includes(normalized as SupportedLocale)) {
      return normalized as SupportedLocale
    }
  }

  return 'en'
}

export function detectWpmlLocale(): SupportedLocale {
  const raw =
    COOKIE_CANDIDATES.map(getCookie).find(
      v => typeof v === 'string' && v.trim().length > 0 && v !== 'all'
    ) ?? ''

  const base = raw.trim().toLowerCase().split(/[-_]/)[0] ?? ''

  // Normalize WPML / common variants
  const normalized =
    base === 'se' ? 'sv' : base === 'nb' || base === 'nn' ? 'no' : base === 'dk' ? 'da' : base

  const supported: SupportedLocale[] = ['en', 'sv', 'es', 'de', 'fr', 'nl', 'fi', 'no', 'da', 'pl']
  return (supported.includes(normalized as SupportedLocale) ? normalized : 'en') as SupportedLocale
}

export function detectLocale(): SupportedLocale {
  // First try WPML detection (for WordPress sites)
  const wpmlLocale = detectWpmlLocale()
  if (wpmlLocale !== 'en') return wpmlLocale

  // Fallback to browser language detection
  return detectBrowserLocale()
}
